import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthContext } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, X } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

interface RegisterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoginClick?: () => void;
}

export const RegisterModal = ({ open, onOpenChange, onLoginClick }: RegisterModalProps) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const navigate = useNavigate();

  const { signUp } = useAuthContext();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    try {
      const { error } = await signUp(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );

      if (!error) {
        const email = formData.email.trim();

        if (newsletterConsent && email) {
          supabase.functions
            .invoke("subscribe-newsletter", {
              body: { email, full_name: "", source: "register_modal" },
            })
            .catch((err) => console.error("[Newsletter] register opt-in failed:", err));
        }

        setFormData({ firstName: "", lastName: "", email: "", password: "" });
        setNewsletterConsent(false);
        onOpenChange(false);
        navigate(`/waiting-confirmation?email=${encodeURIComponent(email)}`);
      }
    } catch (err) {
      console.error("Registration error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 bg-white rounded-2xl">
        <DialogClose
          className="absolute top-4 right-4 rounded-md opacity-70 ring-1 ring-black/10 hover:opacity-100 focus:outline-none transition-opacity z-10"
          aria-label="Stäng"
        >
          <X className="h-5 w-5" />
        </DialogClose>
        <div className="pt-14 px-8 pb-8">
          <DialogHeader className="mb-8 text-center sm:text-center">
            <DialogTitle className="text-center text-2xl font-semibold text-dark-blue">
              Registrera dig - helt gratis
            </DialogTitle>
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                navigate('/anslut-ditt-foretag', { replace: true });
              }}
              className="mx-auto mt-2 inline-block text-sm text-[#A9C6CC] hover:underline"
            >
              Eller är du en leverantör?
            </button>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First Name and Last Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">
                  Förnamn
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#5a8a8a] focus:ring-0"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">
                  Efternamn
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#5a8a8a] focus:ring-0"
                  required
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                E-postadress
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#5a8a8a] focus:ring-0"
                required
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Lösenord
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#5a8a8a] focus:ring-0"
                required
              />
            </div>

            {/* Register Button */}
            <Button
              type="submit"
              disabled={isLoading}
              variant="cream-tool"
              className="w-full py-3 text-base"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Skapar konto...
                </>
              ) : (
                "Registrera"
              )}
            </Button>

            <div className="flex gap-3 items-start">
              <Checkbox
                id="register-newsletter-consent"
                checked={newsletterConsent}
                onCheckedChange={(v) => setNewsletterConsent(v === true)}
                disabled={isLoading}
                className="mt-0.5 border-gray-400 rounded-sm"
              />
              <Label
                htmlFor="register-newsletter-consent"
                className="text-[13px] leading-snug text-gray-500 font-normal cursor-pointer"
              >
                Jag vill prenumerera på Weddinglys nyhetsbrev och godkänner att Weddingly lagrar min e-postadress.{" "}
                <Link
                  to="/privacy-policy"
                  className="underline underline-offset-2 text-gray-600 hover:text-gray-900"
                >
                  Integritetspolicy
                </Link>
              </Label>
            </div>
          </form>

          {/* Privacy Policy Text */}
          <div className="mt-6 text-center text-xs text-gray-600">
            <p>
              Genom att registrera dig accepterar du användning av cookies för att hålla dig inloggad samt godkänner vår{" "}
              <button className="text-[#A9C6CC] hover:underline">
                integritetspolicy
              </button>{" "}
              och våra{" "}
              <button className="text-[#A9C6CC] hover:underline">
                allmänna villkor
              </button>
              .
            </p>
          </div>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-base text-gray-600">
              Har du redan ett konto?{" "}
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onLoginClick?.();
                }}
                className="text-[#A9C6CC] hover:underline font-medium"
              >
                Logga in här
              </button>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
